import React, { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams, useSearchParams } from "react-router-dom"

import { CircularProgress } from "@mui/material"
import Grid from "@mui/material/Grid"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { InputText, Pagination, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { InfoContainer } from "../../../../components/core/InfoContainer"
import { KonanEmptyState } from "../../../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../../../services/KonanAPI"
import type {
  CategoricalType,
  DatetimeType,
  DiscreteOrContinuousType,
  StatisticsResponse,
  TaskId,
  UnknownType,
} from "../../../../types/custom/projects"
import { type StandardErrorType, withErrorHandler } from "../../../../utils/ErrorHandling"
import { sortFeatures } from "../../../../utils/deploymentDetailsHelpers"
import { CategoricalFeatureCard } from "./components/CategoricalFeatureCard"
import { ContinuousOrDiscreteFeatureCard } from "./components/ContinuousOrDiscreteFeatureCard"
import { DatetimeFeatureCard } from "./components/DatetimeFeatureCard"
import { UnknownFeatureCard } from "./components/UnknownFeatureCard"

import styles from "./DataMonitor.module.scss"

export function DataMonitor(): React.ReactElement {
  const { t } = useTranslation("liveMonitoring")
  const [searchParams] = useSearchParams()

  const { id: projectId } = useParams<{ id: string }>()

  const startDate = searchParams.get("startDate")
  const endDate = searchParams.get("endDate")
  const pageSize = 5

  const [page, setPage] = useState<number>(0)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(false)

  // Run Statistics Task
  const {
    isLoading: isLoadingCreatingStatisticsRun,
    data: taskID,
    isError: isCreatingStatisticsRunError,
  } = useQuery<TaskId, AxiosError>(
    ["taskId", projectId, startDate, endDate],
    () =>
      KonanAPI.createStatisticsRun({
        start_date: startDate as string,
        end_date: endDate as string,
        project_uuid: projectId as string,
      }),
    {
      enabled: !!startDate && !!endDate && !!projectId,
      refetchOnMount: true,

      retry: false,
    },
  )

  // Fetch Statistics Results
  const {
    isLoading: isLoadingStatistics,
    data: stats,
    isError: isFetchingStatisticsError,
    remove: removeResults,
  } = useQuery<AxiosResponse<StatisticsResponse>, AxiosError<StandardErrorType>>(
    ["stats", taskID],
    () => KonanAPI.fetchStatistics(taskID?.task_id as string),
    {
      enabled: taskID !== undefined,
      refetchInterval: refetchInterval,
      refetchIntervalInBackground: true,
      refetchOnMount: false,
      // Turn off refetching if an error occurs
      onError: withErrorHandler(() => {
        setRefetchInterval(false)
      }),
      onSuccess: (data) => {
        if (data.data.task_status === "PENDING") {
          setRefetchInterval(3000)
        } else setRefetchInterval(false)
      },
    },
  )

  const statistics = React.useMemo(() => {
    if (stats?.data.task_status === "SUCCESS") {
      return sortFeatures(stats.data.task_result)
    }
    return []
  }, [stats])

  const isLoading = React.useMemo(() => {
    return isLoadingCreatingStatisticsRun || isLoadingStatistics || stats?.data.task_status === "PENDING"
  }, [isLoadingCreatingStatisticsRun, isLoadingStatistics, stats])

  const isError = React.useMemo(() => {
    return isFetchingStatisticsError || isCreatingStatisticsRunError
  }, [isFetchingStatisticsError, isCreatingStatisticsRunError])

  const filterColumnsByName = (
    arrayOfColumns: (DiscreteOrContinuousType | CategoricalType | DatetimeType | UnknownType)[],
    selectedColumns: string[],
  ): (DiscreteOrContinuousType | CategoricalType | DatetimeType | UnknownType)[] => {
    if (!selectedColumns || !selectedColumns.length) {
      return statistics
    }
    const tmp = []
    for (let i = 0; i < arrayOfColumns.length; i++) {
      for (let j = 0; j < selectedColumns.length; j++) {
        if (arrayOfColumns[i]?.name === selectedColumns[j]) {
          tmp.push(arrayOfColumns[i])
        }
      }
    }
    //Reset page to 1 when filtering
    if (parseInt(tmp.length / 10 + "") < page / 10) {
      setPage(0)
    }

    return tmp.filter(
      (column): column is DiscreteOrContinuousType | CategoricalType | DatetimeType | UnknownType =>
        column !== undefined,
    )
  }

  // Clear statistics cache when date range changes
  useEffect(() => {
    if (removeResults) {
      removeResults()
    }
  }, [startDate, endDate, removeResults])

  const [value, setValue] = useState<string | number>("")

  return (
    <Grid container spacing={2}>
      {/* Search + DateRange Section*/}
      <Grid container item xs={12} justifyContent={"space-between"}>
        <Grid item xs={12} sm={8} md={4}>
          <Grid container item direction={"column"}>
            <Grid item>
              <InputText
                placeholder={t("DataMonitor.inputs.features.placeholder")}
                hideDescription
                disabledTipTitle={t("DataMonitor.inputs.features.disabledTipTitle")}
                disabled={isLoading}
                width={300}
                value={value}
                variant="filled"
                handleChange={(e: InputChangeEvent) => setValue(e.target.value)}
                label={t("DataMonitor.inputs.features.label")}
                multipleModeProps={{
                  isActive: true,
                  selectedValues: selectedColumns,
                  setSelectedValues: (selectedOptions: (string | number)[]) => {
                    setSelectedColumns(selectedOptions as string[])
                  },
                }}
                noEntriesProps={{
                  shouldAcceptNewValues: false,
                }}
                options={statistics.map(
                  (statistic: DiscreteOrContinuousType | CategoricalType | DatetimeType | UnknownType) =>
                    statistic.name,
                )}
              />
            </Grid>

            <Grid container item xs={12} sm={6} md={3} alignItems="flex-end" mt={1}>
              {stats?.data.task_result?.num_of_examples && (
                <Typography variant="p" gutterBottom>
                  {t("DataMonitor.stats.calculatedOnPrefix")}{" "}
                  <strong>{stats.data.task_result?.num_of_examples.toLocaleString()}</strong>{" "}
                  {t("DataMonitor.stats.samplesSuffix")}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Info Section */}
      {isLoading && !isError ? (
        <Grid item xs={12} className={"empty-container"}>
          <InfoContainer
            icon={<CircularProgress className={styles.circularProgress} />}
            title={t("DataMonitor.info.calculating")}
          />
        </Grid>
      ) : isError ? (
        <Grid item xs={12} className={"empty-container"}>
          <KonanEmptyState
            title={t("DataMonitor.empty.error.title")}
            subTitle={t("DataMonitor.empty.error.subtitle")}
          />
        </Grid>
      ) : !isLoading && !isError && statistics?.length === 0 ? (
        <Grid item xs={12} className={"empty-container"}>
          <KonanEmptyState
            title={t("DataMonitor.empty.noFeatures.title")}
            subTitle={t("DataMonitor.empty.noFeatures.subtitle")}
          />
        </Grid>
      ) : (
        ""
      )}

      {/* Card Rendering Section */}
      {filterColumnsByName(statistics, selectedColumns)
        .slice(page * pageSize, (page + 1) * pageSize)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((feature: any) =>
          feature.type === "discrete" || feature.type === "continuous" ? (
            <Grid item xs={12} key={feature.name}>
              <ContinuousOrDiscreteFeatureCard
                name={feature.name}
                type={feature.type}
                num_of_examples={feature.num_of_examples}
                min={feature.min}
                max={feature.max}
                mean={feature.mean}
                median={feature.median}
                num_zeros={feature.num_zeros}
                num_missing={feature.num_missing}
                std_dev={feature.std_dev}
                histogram={feature.histogram}
                parents={feature.parent}
              />
            </Grid>
          ) : feature.type === "categorical" ? (
            <Grid item xs={12} key={feature.name}>
              <CategoricalFeatureCard
                name={feature.name}
                num_of_examples={feature.num_of_examples}
                num_non_missing={feature.num_non_missing}
                unique={feature.unique}
                avg_length={feature.avg_length}
                rank_histogram={feature.rank_histogram}
                parents={feature.parent}
              />
            </Grid>
          ) : feature.type === "datetime" ? (
            <Grid item xs={12} key={feature.name}>
              <DatetimeFeatureCard
                name={feature.name}
                min={feature.min}
                max={feature.max}
                span={feature.span}
                num_of_examples={feature.num_of_examples}
                num_missing={feature.num_missing}
                unique={feature.unique}
                frequency={feature.aggregate_by}
                aggregates={feature.aggregates}
                parents={feature.parent}
              />
            </Grid>
          ) : (
            <Grid item xs={12} key={feature.name}>
              <UnknownFeatureCard name={feature.name} parents={feature.parent} />
            </Grid>
          ),
        )}

      {/* Pagination Section */}
      {statistics?.length > 0 && !isLoading && statistics && (
        <Grid container item justifyContent="flex-end">
          <Pagination
            page={page + 1}
            pageSize={pageSize}
            totalCount={!selectedColumns || !selectedColumns.length ? statistics?.length : selectedColumns.length}
            onPageChange={(page: number) => {
              setPage(page - 1)
              window.scrollTo(0, 0)
            }}
            variant={"simple"}
            hidePageSize={true}
          />
        </Grid>
      )}
    </Grid>
  )
}
